import React, { useEffect, useState } from 'react';
import T from 'i18n';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentCaseId, selectCurrentFolder, selectParams } from 'common/selectors';
import { Controller, useForm, useWatch } from 'react-hook-form';
import * as actions from '../../redux/actions';
import { Spinner } from 'features/common';
import SelectFormAdapter from 'features/common/SelectFormAdapter';
import { Col, Form, Row } from 'react-bootstrap';
import { Checkbox, FormControlLabel } from '@mui/material';
import saveAs from 'save-as';
import * as actionsV from 'features/viewing/redux/actions';
import { Modal } from 'features/common/MaterialBasedComponents';
import { useUpdateUserSettings } from 'features/common/redux/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

export const LegacyFileDownload = ({ show, handleClose, selectedRows }) => {
  const dispatch = useDispatch();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fileVersions, setFileVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const { file } = useSelector(selectParams);
  const currentFolder = useSelector(selectCurrentFolder);
  const currentCaseId = useSelector(selectCurrentCaseId);
  const { userSettings, updateUserSettings } = useUpdateUserSettings();
  const activeVersion = fileVersions.find((itm) => itm.active);
  const containsPublicHyperlink = selectedRows[0]?.hasPublicHyperlinks;
  const nonDummyFileVersions = fileVersions.filter(
    (version) => !version.type || version.type !== 'dummy',
  );
  const isMergeOnlySource =
    nonDummyFileVersions.length === 1 &&
    (nonDummyFileVersions[0]?.source || '').toLowerCase() === 'merge';

  const defaultFileName = 'original';
  const downloadSettingsByCase = userSettings?.dataRoom?.downloadSettingsByCase || {};
  const savedDownloadSettings =
    currentCaseId && downloadSettingsByCase[currentCaseId]
      ? downloadSettingsByCase[currentCaseId]
      : {};
  const hasSavedDownloadSettings = Object.keys(savedDownloadSettings).length > 0;
  const savedFileSource = savedDownloadSettings.fileSource || '';
  const savedIncludeFileDetails = savedDownloadSettings.includeFileDetails ?? true;
  const savedIncludeExternalId = savedDownloadSettings.includeFileDetails ?? false;
  const savedIncludeCrossRef = savedDownloadSettings.includeCrossRef ?? false;
  const savedConvertCrossRefToHyperlink =
    typeof savedDownloadSettings.convertCrossRefToHyperlink === 'boolean'
      ? savedDownloadSettings.convertCrossRefToHyperlink
      : false;
  const savedIncludePublicHyperlinks =
    typeof savedDownloadSettings.includePublicHyperlinks === 'boolean'
      ? savedDownloadSettings.includePublicHyperlinks
      : false;

  const getFileNameDisplayName = (option) => {
    return T.translate(option.name);
  };

  const getFileSourceLabel = (source) => {
    if (source === 'ocr') {
      return T.translate('generic.downloadFormatOcr');
    }
    if (source === 'original (pdf)') {
      return T.translate('generic.downloadFormatOriginalPdf');
    }
    if (source === 'tiff') {
      return T.translate('generic.downloadFormatTiff');
    }
    return source;
  };

  const fileNameOptions = [
    {
      name: 'case.downloadFolderModal.fileNameOptions.originalFileName',
      fileName: 'original',
    },
    currentFolder?.id && {
      name: 'case.downloadFolderModal.fileNameOptions.bundleTabFileNameLabel',
      fileName: 'bundleTabFilename',
    },
    { name: 'case.downloadFolderModal.fileNameOptions.trialviewID', fileName: 'trialviewId' },
  ].filter(Boolean);

  const availableFileNameOptions = new Set(fileNameOptions.map((option) => option.fileName));
  const savedFilenameSelector = availableFileNameOptions.has(
    savedDownloadSettings?.filenameSelector,
  )
    ? savedDownloadSettings.filenameSelector
    : defaultFileName;

  const { register, reset, handleSubmit, control, setValue } = useForm({
    defaultValues: {
      fileSource: savedFileSource,
      burnId: savedIncludeFileDetails,
      burnExternalId: savedIncludeExternalId,
      includeFileDetails: savedIncludeFileDetails,
      filenameSelector: savedFilenameSelector,
      burnCrossRef: savedIncludeCrossRef,
      hyperlink: savedConvertCrossRefToHyperlink,
      publichyperlink: savedIncludePublicHyperlinks,
      saveDownloadSettings: false,
    },
  });
  const burnCrossRefValue = useWatch({ control, name: 'burnCrossRef' });
  const showHyperlinkToggle = !!burnCrossRefValue;

  const downloadActiveFileVersion = (data) => {
    const includeFileDetailsValue = isMergeOnlySource ? false : data.includeFileDetails;
    setLoading(true);
    if (data.saveDownloadSettings) {
      const safeUserSettings = userSettings || {};
      const currentDataRoom = safeUserSettings.dataRoom || {};
      const currentDownloadSettingsByCase = currentDataRoom.downloadSettingsByCase || {};
      updateUserSettings({
        userSettings: {
          ...safeUserSettings,
          dataRoom: {
            ...currentDataRoom,
            downloadSettingsByCase: {
              ...currentDownloadSettingsByCase,
              ...(currentCaseId && {
                [currentCaseId]: {
                  fileSource: data.fileSource || selectedVersion || activeVersion?.source || '',
                  includeFileDetails: includeFileDetailsValue,
                  includeExternalId: data.burnExternalId,
                  filenameSelector: data.filenameSelector || defaultFileName,
                  includeCrossRef: data.burnCrossRef,
                  convertCrossRefToHyperlink: data.hyperlink,
                  includePublicHyperlinks: data.publichyperlink,
                },
              }),
            },
          },
        },
      });
    }
    dispatch(
      actionsV.downloadDocument({
        fileId: selectedRows[0].id || file,
        legacy: true,
        version: activeVersion.source,
        versionId: activeVersion.id,
        fileType: selectedRows[0].type,
        burnId: includeFileDetailsValue ? data.burnId : false,
        burnExternalId: data.burnExternalId,
        burnCrossRef: data.burnCrossRef,
        hyperlink: data.hyperlink,
        publichyperlink: data.publichyperlink,
        filenameSelector: data.filenameSelector || defaultFileName,
        includeFileDetails: includeFileDetailsValue,
        ...(currentFolder?.id && { folderId: currentFolder.id }),
      }),
    ).then((response) => {
      return response && saveAs(response.blob, response.fileName || selectedRows[0].id || file);
    });
    setTimeout(() => {
      handleClose();
      setLoading(false);
    }, 500);
  };

  const updateFileVersions = (versionId) => {
    const updatedFileVersions = fileVersions.map((item) => {
      if (item.id === versionId) return { ...item, active: true };
      else return { ...item, active: false };
    });
    setFileVersions(updatedFileVersions);
  };

  useEffect(() => {
    dispatch(actions.fetchFileVersions({ fileId: selectedRows[0].id || file }))
      .then((res) => {
        const nonDummyVersions = res.filter((version) => !version.type || version.type !== 'dummy');
        const savedVersion = savedFileSource
          ? nonDummyVersions.find((item) => item.source === savedFileSource)
          : null;
        const defaultSearchableVersion = !hasSavedDownloadSettings
          ? nonDummyVersions.find((item) =>
              ['ocr', 'recent-ocr'].includes((item.source || '').toLowerCase()),
            )
          : null;
        const defaultOriginalVersion = !hasSavedDownloadSettings
          ? nonDummyVersions.find((item) =>
              ['original', 'original (pdf)'].includes((item.source || '').toLowerCase()),
            )
          : null;
        const preferredVersion =
          savedVersion ||
          defaultSearchableVersion ||
          defaultOriginalVersion ||
          nonDummyVersions.find((item) => item.active) ||
          nonDummyVersions[0];
        const updated = res.map((item) =>
          preferredVersion && item.id === preferredVersion.id
            ? { ...item, active: true }
            : { ...item, active: false },
        );
        setFileVersions(updated);
        if (preferredVersion) {
          setSelectedVersion(preferredVersion.source);
          setValue('fileSource', preferredVersion.source);
        }
        setValue('filenameSelector', savedFilenameSelector);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [show, selectedRows, dispatch, file, setValue, savedFileSource, savedFilenameSelector]);

  useEffect(() => {
    if (show) {
      reset({
        fileSource: selectedVersion || savedFileSource,
        burnId: savedIncludeFileDetails,
        burnExternalId: savedIncludeExternalId,
        includeFileDetails: savedIncludeFileDetails,
        filenameSelector: savedFilenameSelector,
        burnCrossRef: savedIncludeCrossRef,
        hyperlink: savedConvertCrossRefToHyperlink,
        publichyperlink: savedIncludePublicHyperlinks,
        saveDownloadSettings: false,
      });
    }
  }, [
    show,
    reset,
    savedFileSource,
    savedIncludeFileDetails,
    savedIncludeExternalId,
    savedIncludeCrossRef,
    savedConvertCrossRefToHyperlink,
    savedIncludePublicHyperlinks,
    savedFilenameSelector,
    selectedVersion,
  ]);

  useEffect(() => {
    if (show && isMergeOnlySource) {
      setValue('includeFileDetails', false);
      setValue('burnId', false);
    }
  }, [show, isMergeOnlySource, setValue]);

  return (
    <>
      <Modal
        show={show}
        title={T.translate('generic.download') + ` - ${selectedRows[0].id || file}`}
        handleClose={() => {
          if (loading) setLoading(false);
          setFileVersions([]);
          setSelectedVersion(null);
          handleClose();
        }}
        saveTitle={T.translate('generic.download')}
        handleSave={handleSubmit((data) => downloadActiveFileVersion(data))}
        disableSave={loading || !activeVersion}
      >
        {!loading &&
          (fileVersions && fileVersions.length > 0 ? (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <Form.Group>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <Form.Label
                    style={{
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: '#111827',
                      marginBottom: '0.35rem',
                    }}
                  >
                    {T.translate('generic.downloadFormat')}
                  </Form.Label>
                  <SelectFormAdapter
                    key={`format-${selectedVersion || 'none'}`}
                    control={control}
                    name="fileSource"
                    label=""
                    options={nonDummyFileVersions.map((version) => ({
                      label: getFileSourceLabel(version.source),
                      value: version.source,
                    }))}
                    defaultValue={selectedVersion || ''}
                    placeholder={T.translate('generic.selectFileSource')}
                    onChange={(value) => {
                      const selected = fileVersions.find((version) => version.source === value);
                      if (selected) {
                        setSelectedVersion(selected.source);
                        updateFileVersions(selected.id);
                      }
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {/* <hr style={{ width: '100%', border: '0', borderTop: '1px solid #e5e7eb' }} /> */}
                  <button
                    type="button"
                    onClick={() => setShowAdvanced((prev) => !prev)}
                    style={{
                      alignSelf: 'flex-start',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      background: 'transparent',
                      border: 'none',
                      color: '#748CEC',
                      fontWeight: 700,
                      padding: 0,
                      cursor: 'pointer',
                    }}
                  >
                    <span>
                      {showAdvanced
                        ? T.translate('generic.hideAdvancedSettings')
                        : T.translate('generic.showAdvancedSettings')}
                    </span>
                    <FontAwesomeIcon icon={showAdvanced ? faChevronUp : faChevronDown} size="sm" />
                  </button>

                  {showAdvanced && (
                    <>
                      <div
                        style={{
                          color: '#6b7280',
                          fontSize: '0.9rem',
                        }}
                      >
                        {T.translate('generic.downloadFormatHelper')}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Form.Label
                          style={{
                            fontWeight: 700,
                            fontSize: '1rem',
                            color: '#111827',
                            marginBottom: '0.25rem',
                          }}
                        >
                          {T.translate('generic.downloadDetails')}
                        </Form.Label>
                        {!isMergeOnlySource && (
                          <Controller
                            control={control}
                            name="includeFileDetails"
                            render={({ field }) => (
                              <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                  <Checkbox
                                    checked={!!field.value}
                                    onChange={(event, checked) => {
                                      field.onChange(checked);
                                      setValue('burnId', checked);
                                    }}
                                    defaultChecked
                                  />
                                }
                                label={T.translate('generic.downloadIncludeFileDetails')}
                              />
                            )}
                          />
                        )}
                        <Controller
                          control={control}
                          name="burnExternalId"
                          render={({ field }) => (
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={
                                <Checkbox
                                  checked={!!field.value}
                                  onChange={(event, checked) => field.onChange(checked)}
                                />
                              }
                              label={T.translate('generic.downloadIncludeExternalId')}
                            />
                          )}
                        />
                        {activeVersion && activeVersion.crossRefCount > 0 && (
                          <>
                            <Controller
                              control={control}
                              name="burnCrossRef"
                              render={({ field }) => (
                                <FormControlLabel
                                  sx={{ m: 0 }}
                                  control={
                                    <Checkbox
                                      checked={!!field.value}
                                      onChange={(event, checked) => {
                                        field.onChange(checked);
                                        if (!checked) {
                                          setValue('hyperlink', false);
                                        }
                                      }}
                                    />
                                  }
                                  label={T.translate(`case.includeCrossRef`)}
                                />
                              )}
                            />
                            <Controller
                              control={control}
                              name="hyperlink"
                              render={({ field }) => (
                                <FormControlLabel
                                  sx={{ m: 0 }}
                                  control={
                                    <Checkbox
                                      checked={!!field.value}
                                      onChange={(event, checked) => field.onChange(checked)}
                                      disabled={!showHyperlinkToggle}
                                    />
                                  }
                                  label={T.translate(`case.convertCrossRefToExternalHyperlink`)}
                                />
                              )}
                            />
                          </>
                        )}
                        {containsPublicHyperlink && (
                          <Controller
                            control={control}
                            name="publichyperlink"
                            render={({ field }) => (
                              <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                  <Checkbox
                                    checked={!!field.value}
                                    onChange={(event, checked) => field.onChange(checked)}
                                  />
                                }
                                label={T.translate(`case.includePublicHyperLinks`)}
                              />
                            )}
                          />
                        )}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                        <Form.Label
                          style={{
                            fontWeight: 700,
                            fontSize: '1rem',
                            color: '#111827',
                            marginBottom: '0.25rem',
                          }}
                        >
                          {T.translate('common.fileName')}
                        </Form.Label>
                        <SelectFormAdapter
                          key={`filename-${defaultFileName || 'none'}`}
                          control={control}
                          name="filenameSelector"
                          label=""
                          options={fileNameOptions.map((value) => ({
                            label: getFileNameDisplayName(value),
                            value: value.fileName,
                          }))}
                          defaultValue={defaultFileName}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                        <div
                          style={{
                            color: '#6b7280',
                            fontSize: '0.9rem',
                          }}
                        >
                          {T.translate('generic.downloadFilenameHelper')}
                        </div>
                      </div>
                      <Controller
                        control={control}
                        name="saveDownloadSettings"
                        render={({ field }) => (
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                checked={!!field.value}
                                onChange={(event, checked) => field.onChange(checked)}
                              />
                            }
                            label={T.translate('generic.saveDownloadSettings')}
                          />
                        )}
                      />
                    </>
                  )}
                </div>
              </Form.Group>
            </Form>
          ) : (
            T.translate('generic.noFileVersionsFound')
          ))}
        {loading && <Spinner />}
      </Modal>
    </>
  );
};

export default LegacyFileDownload;
